<script setup lang="ts">
import { computed } from 'vue'

import ContentHeading from '@fondof-ui/components/content/ContentHeading.vue'
import ContentHeadingRoot from '@fondof-ui/components/content/ContentHeadingRoot.vue'
import ContentRoot from '@fondof-ui/components/content/ContentRoot.vue'
import ContentText from '@fondof-ui/components/content/ContentText.vue'
import MediaImage from '@fondof-ui/components/media/MediaImage.vue'
import MediaRoot from '@fondof-ui/components/media/MediaRoot.vue'
import { default as Container } from '@storefront-satch/components/molecules/content-builder/ContentBuilderContainer.vue'
import TeaserCardGroupTypeResolver from '@storefront-satch/components/molecules/teaser-card/TeaserCardGroupTypeResolver.vue'
import type {
  TeaserCardItemAdditionalProps,
  TeaserCardItemProps,
} from '@storefront-satch/components/molecules/teaser-card/types'
import type { TeaserCardGroupProps } from '@storefront-satch/components/organisms/teaser-card-group/types'
import { useTeaserCard } from '@storefront-satch/composables/teaser-card/useTeaserCard'
import { clsx } from 'clsx'

const props = withDefaults(
  defineProps<TeaserCardGroupProps & TeaserCardItemAdditionalProps>(),
  {
    showActive: false,
  }
)

const emit = defineEmits<{
  (e: 'select', id: string): void
}>()

const type = computed(() => props.type ?? 'slider')
const teaserCardItems = computed<
  (TeaserCardItemProps & TeaserCardItemAdditionalProps)[]
>(() => {
  return (
    props.teaserCardItemsCollection?.items.filter((item) => item !== null) || []
  )
})

const { teaserCardItem: teaserCardItemComponent } = useTeaserCard(props.size)
</script>

<template>
  <div
    :id="props.sys?.id"
    :data-cfid="props.sys?.id"
    data-label="Teaser Card Group"
    :data-anchor="props.anchorId"
    :class="['w-full justify-center', { relative: props.backgroundImage }]"
  >
    <div
      v-if="props.backgroundImage"
      class="absolute inset-0 flex max-w-[theme(screens[2xl])]"
    >
      <MediaRoot
        class="w-full"
        :entry="props.backgroundImage"
      >
        <template #image="{ data }">
          <MediaImage
            v-bind="data"
            class="size-full object-cover"
          />
        </template>
      </MediaRoot>
    </div>

    <Container
      :class="[
        'relative mx-auto my-0 flex w-full max-w-[theme(screens[2xl])] flex-wrap',
        {
          'max-sm:px-0': type === 'slider',
        },
      ]"
      :style="
        props.backgroundColor && `background-color: ${props.backgroundColor}`
      "
      :remove-space="props.removeSpace"
    >
      <ContentRoot
        class="m-0 flex w-full flex-wrap md:order-none lg:w-2/3"
        :class="{
          'px-5 sm:px-0': type === 'slider',
        }"
      >
        <ContentHeadingRoot
          v-slot="{ headlineElement, sublineElement }"
          :headline-level="props.headlineLevel || '2'"
        >
          <ContentHeading
            :is="headlineElement"
            v-if="props.headline || props.subline"
            :class="
              clsx([
                'w-full flex-1 break-words text-lg !leading-[1.3] text-black sm:hyphens-none',
                {
                  'lg:mb-[30px]': props.size && props.size === 'medium',
                  'font-sans lg:mb-[50px]': !props.subline,
                  'flex flex-col md:mb-5': props.size && props.size === 'large',
                  'font-sans-bold': props.size === 'large' && props.subline,
                  'mb-7.5 font-sans': props.size === 'large' && !props.subline,
                  'font-sans-semi-bold':
                    props.size && props.size !== 'large' && props.subline,
                  'mb-[25px] sm:mb-[30px] lg:mb-10':
                    !props.subline && !props.text && props.size !== 'large',
                  'font-sans-bold mb-1': props.subline,
                  'mb-1': props.size === 'large',
                },
              ])
            "
          >
            {{ props.headline }}
          </ContentHeading>
          <ContentHeading
            :is="sublineElement"
            v-if="props.subline"
            :class="[
              'text-md block w-full font-sans !leading-[1.3]',
              {
                'mb-[25px] sm:mb-[30px] lg:mb-10':
                  !props.text && props.size !== 'large',
                'mb-7.5 sm:mb-12.5': !props.text && props.size === 'large',
                'order-2': props.size && props.size === 'large',
                'mb-[0.25em] text-base font-extrabold':
                  props.size && props.size !== 'large',
              },
            ]"
          >
            {{ props.subline }}
          </ContentHeading>
        </ContentHeadingRoot>
        <ContentText
          v-if="props.text"
          :content="props.text || ''"
          class="prose-p:text-sm props-p:mb-0 prose-p:max-w-[70ex] mb-[50px] text-sm md:mb-20 lg:mb-10"
        />
      </ContentRoot>
      <TeaserCardGroupTypeResolver
        v-if="teaserCardItems.length > 0"
        :type="props.type"
        :size="props.size"
        :teaser-card-items="teaserCardItems"
        :selected-item="props.selectedId"
        :show-active="props.showActive"
      >
        <template
          v-for="(teaserCardItem, index) in teaserCardItems"
          :key="teaserCardItem.sys.id"
          #[`${teaserCardItem.sys.id}__${index}`]
        >
          <component
            :is="teaserCardItemComponent"
            v-bind="{
              ...teaserCardItem,
              showNumber: props.showNumbers,
              number: index + 1,
              size: props.size,
              groupType: props.type,
              contentStyle: teaserCardItem.contentStyle,
              showActive: props.showActive,
              isActive: props.selectedId === teaserCardItem.sys.id,
            }"
            @select="emit('select', teaserCardItem.sys.id)"
          />
        </template>
      </TeaserCardGroupTypeResolver>
    </Container>
  </div>
</template>
